import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Fade
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  People as PeopleIcon,
  Close as CloseIcon,
  EventAvailable as VacationIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Notifications as NotificationsIcon,
  // Groups as GroupsIcon // ZAKOMENTOWANE
} from '@mui/icons-material';
import useAppStore from '../store/useAppStore';
import WorkCalendar from '../components/WorkCalendar';
import VacationCalendar from '../components/VacationCalendar';
// import VacationTeamView from '../components/VacationTeamView'; // ZAKOMENTOWANE
import EmployeeManager from '../components/EmployeeManager';
import UndoRedoToolbar from '../components/UndoRedoToolbar';
import UserInfo from '../components/UserInfo';
import NotificationSettings from '../components/NotificationSettings';
import ChangelogCleanupButton from '../components/ChangelogCleanupButton';
import { employeeAPI, workTypeAPI } from '../services/apiWithAuth';
import { useAuth } from '../hooks/useAuth';

const WorkSchedulePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  
  // Mapowanie ścieżek URL na indeksy zakładek
  const getTabFromPath = (pathname) => {
    switch (pathname) {
      case '/kalendarz':
        return 0;
      case '/urlopy':
        return 1;
      case '/pracownicy':
        return 2;
      default:
        return 0;
    }
  };

  // Mapowanie indeksów zakładek na ścieżki URL
  const getPathFromTab = (tabIndex) => {
    switch (tabIndex) {
      case 0:
        return '/kalendarz';
      case 1:
        return '/urlopy';
      case 2:
        return '/pracownicy';
      default:
        return '/kalendarz';
    }
  };

  const [activeTab, setActiveTab] = useState(getTabFromPath(location.pathname));
  // const [vacationSubTab, setVacationSubTab] = useState(0); // Podzakładka dla kalendarza urlopowego - ZAKOMENTOWANE
  const [notificationSettingsOpen, setNotificationSettingsOpen] = useState(false);
  const {
    employees,
    workTypes,
    notification,
    darkMode,
    isInitializing,
    loadingEmployees,
    loadingWorkTypes,
    setEmployees,
    setWorkTypes,
    setNotification,
    clearNotification,
    toggleTheme,
    loadHolidays,
    setInitializing,
    setLoadingEmployees,
    setLoadingWorkTypes,
  } = useAppStore();

  // Synchronizuj zakładkę z URL-em przy zmianie ścieżki
  useEffect(() => {
    const newTab = getTabFromPath(location.pathname);
    setActiveTab(newTab);
  }, [location.pathname]);

  const loadInitialData = useCallback(async () => {
    try {
      setLoadingEmployees(true);
      setLoadingWorkTypes(true);
      
      // Load employees, work types and holidays in parallel
      const currentYear = new Date().getFullYear();
      const [employeesData, workTypesData] = await Promise.all([
        employeeAPI.getAll(),
        workTypeAPI.getAll(),
        loadHolidays(currentYear) // Load holidays for current year
      ]);
      
      setEmployees(employeesData);
      setWorkTypes(workTypesData);
    } catch (error) {
      console.error('Error loading initial data:', error);
      setNotification({
        type: 'error',
        message: 'Błąd podczas ładowania danych aplikacji'
      });
    } finally {
      setLoadingEmployees(false);
      setLoadingWorkTypes(false);
      setInitializing(false);
    }
  }, [setEmployees, setWorkTypes, setNotification, loadHolidays, setLoadingEmployees, setLoadingWorkTypes, setInitializing]);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    navigate(getPathFromTab(newValue));
    // Reset vacation sub-tab when switching main tabs
    // if (newValue !== 1) {
    //   setVacationSubTab(0);
    // }
  };

  // const handleVacationSubTabChange = (event, newValue) => {
  //   setVacationSubTab(newValue);
  // };

  const handleCloseNotification = () => {
    clearNotification();
  };

  const getActiveEmployeesCount = () => {
    return employees.filter(emp => emp.is_active).length;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', overflow: 'hidden' }}>
      {/* App Bar */}
      <AppBar position="static" elevation={1}>
        <Toolbar sx={{ minHeight: { xs: 48, sm: 64 }, px: { xs: 1, sm: 2 } }}>
          <CalendarIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Kalendarz Pracy
          </Typography>
          
          {/* User Info */}
          <UserInfo />
          
          <Box sx={{ ml: 1, display: 'flex', gap: 1 }}>
            {/* Przyciski tylko dla administratorów */}
            {hasRole('Administrator') && (
              <>
                <ChangelogCleanupButton />
                
                <IconButton
                  color="inherit"
                  onClick={() => setNotificationSettingsOpen(true)}
                  title="Ustawienia powiadomień (tylko dla administratorów)"
                  sx={{ 
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  <NotificationsIcon />
                </IconButton>
              </>
            )}
          </Box>
          
          <IconButton
            color="inherit"
            onClick={toggleTheme}
            title={darkMode ? 'Przełącz na motyw jasny' : 'Przełącz na motyw ciemny'}
            sx={{ 
              ml: 1,
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container 
        maxWidth={false} 
        sx={{ 
          mt: { xs: 0.5, sm: 2 }, 
          mb: { xs: 0.5, sm: 2 }, 
          px: { xs: 0.25, sm: 1, md: 2 }, 
          flexGrow: 1 
        }}
      >
        <Paper 
          sx={{ 
            borderRadius: { xs: 0, sm: 2 }, 
            overflow: 'hidden',
            width: '100%',
          }}
        >
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', overflowX: 'auto' }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab
                icon={<CalendarIcon />}
                label="Kalendarz"
                iconPosition="start"
              />
              <Tab
                icon={<VacationIcon />}
                label="Kalendarz Urlopowy"
                iconPosition="start"
              />
              <Tab
                icon={<PeopleIcon />}
                label="Pracownicy"
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box sx={{ p: { xs: 0.25, sm: 1, md: 2 } }}>
            <Fade in={!isInitializing} timeout={300}>
              <Box>
                {activeTab === 0 && (
                  <Box>
                    {isInitializing || loadingEmployees ? (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: '400px',
                        gap: 2
                      }}>
                        <CircularProgress size={40} />
                        <Typography variant="body1" color="text.secondary">
                          Ładowanie danych...
                        </Typography>
                      </Box>
                    ) : employees.filter(emp => emp.is_active).length === 0 ? (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          Brak aktywnych pracowników
                        </Typography>
                        <Typography variant="body2">
                          Przejdź do zakładki "Pracownicy" aby dodać pierwszego pracownika.
                        </Typography>
                      </Alert>
                    ) : (
                      <Box>
                        <WorkCalendar />
                      </Box>
                    )}
                  </Box>
                )}

                {activeTab === 1 && (
                  <Box>
                    {isInitializing || loadingEmployees ? (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: '400px',
                        gap: 2
                      }}>
                        <CircularProgress size={40} />
                        <Typography variant="body1" color="text.secondary">
                          Ładowanie danych...
                        </Typography>
                      </Box>
                    ) : employees.filter(emp => emp.is_active).length === 0 ? (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          Brak aktywnych pracowników
                        </Typography>
                        <Typography variant="body2">
                          Przejdź do zakładki "Pracownicy" aby dodać pierwszego pracownika.
                        </Typography>
                      </Alert>
                    ) : (
                      <Box>
                        {/* ZAKOMENTOWANE - Widok zespołowy */}
                        {/* <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                          <Tabs value={vacationSubTab} onChange={handleVacationSubTabChange}>
                            <Tab
                              icon={<VacationIcon />}
                              label="Mój urlop"
                              iconPosition="start"
                            />
                            <Tab
                              icon={<GroupsIcon />}
                              label="Widok zespołowy"
                              iconPosition="start"
                            />
                          </Tabs>
                        </Box>

                        {vacationSubTab === 0 && <VacationCalendar />}
                        {vacationSubTab === 1 && <VacationTeamView />} */}
                        
                        {/* Bezpośrednie renderowanie kalendarza urlopowego */}
                        <VacationCalendar />
                      </Box>
                    )}
                  </Box>
                )}

                {activeTab === 2 && (
                  <Box>
                    <EmployeeManager />
                  </Box>
                )}
              </Box>
            </Fade>
          </Box>
        </Paper>
      </Container>

      {/* Notification Snackbar */}
      <Snackbar
        open={!!notification}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {notification && (
          <Alert
            onClose={handleCloseNotification}
            severity={notification.type}
            variant="filled"
            action={
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleCloseNotification}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            {notification.message}
          </Alert>
        )}
      </Snackbar>

      {/* Notification Settings Dialog */}
      <NotificationSettings
        open={notificationSettingsOpen}
        onClose={() => setNotificationSettingsOpen(false)}
      />
    </Box>
  );
};

export default WorkSchedulePage;